import type { AppLocale } from '@ielts-buddy/shared'

type Dict = Record<string, string>

const EN: Dict = {
  'nav.home': 'Home',
  'nav.results': 'Results',
  'nav.leaderboard': 'Board',
  'nav.profile': 'Profile',
  'app.loading': 'Signing you in…',
  'app.signInError': "Couldn't sign you in",
  'app.tryAgain': 'Try again',
  'common.loading': 'Loading…',
  'home.greet.midnight': 'Burning the midnight oil',
  'home.greet.morning': 'Good morning',
  'home.greet.afternoon': 'Good afternoon',
  'home.greet.evening': 'Good evening',
  'home.badge.available': 'Available now',
  'home.badge.soon': 'Soon',
  'home.badge.inDev': 'In development',
  'home.speaking.badge': 'Available now',
  'home.speaking.duration': '~12 min',
  'home.speaking.title': 'Speaking mock exam',
  'home.speaking.blurb': 'Three parts. AI examiner. Detailed band feedback.',
  'home.speaking.ariaHero':
    'Start full Speaking mock exam — three parts, AI examiner, detailed IELTS band feedback',
  'home.quickPractice': 'Quick practice',
  'home.speaking.ariaQuickToggle': 'Expand or collapse quick practice options',
  'home.speaking.ariaQuickPanel': 'Quick practice options',
  'home.tile.warm.title': 'Warm up',
  'home.tile.warm.meta': '~3 min · 2 questions',
  'home.tile.warm.aria': 'Start Warm up — 2 questions, about 3 minutes',
  'home.tile.p1.title': 'Part 1 focus',
  'home.tile.p1.meta': '~5 min · familiar topics',
  'home.tile.p1.aria': 'Start Part 1 focus — familiar topics, about 5 minutes',
  'home.tile.p2.title': 'Part 2 focus',
  'home.tile.p2.meta': '~5 min · cue card',
  'home.tile.p2.aria': 'Start Part 2 focus — cue card, about 5 minutes',
  'home.tile.p3.title': 'Part 3 focus',
  'home.tile.p3.meta': '~5 min · discussion',
  'home.tile.p3.aria': 'Start Part 3 focus — discussion, about 5 minutes',
  'home.module.writing.title': 'Writing',
  'home.module.writing.desc': 'Task 1 + Task 2 with AI feedback.',
  'home.module.listening.title': 'Listening',
  'home.module.listening.desc': '4 sections, official IELTS format.',
  'home.module.reading.title': 'Reading',
  'home.module.reading.desc': '3 passages, 40 questions.',
  'home.footer':
    'Powered by AI scoring against official IELTS band descriptors',
  'results.title': 'Results',
  'results.loadError': 'Could not load your results list.',
  'results.historyNotFound':
    'The results list is unavailable — the server may be out of date or the link is wrong. Check your connection and try again, or update the mini app.',
  'results.historyServerError':
    'The server is temporarily unavailable. Please try again in a moment.',
  'results.historyGeneric':
    'Something went wrong while loading. Check your internet and try again.',
  'results.retry': 'Retry',
  'results.emptyTitle': 'No results yet',
  'results.emptyBody': 'Your mock exam results will appear here.',
  'results.goHome': 'Go to Home',
  'results.backHome': 'Back to home',
  'results.detailLoading': 'Loading your results…',
  'results.detailLoadError': "Couldn't load results",
  'results.kind.full': 'Full test',
  'results.kind.warm': 'Quick practice',
  'results.kind.section': 'Section',
  'results.band': 'Band',
  'results.progressTitle': 'Your band trend',
  'results.progressCaption':
    'Overall band from scored sessions, oldest → newest.',
  'results.progressHint':
    'Finish one more scored session to see how your band moves over time.',
  'results.progressBest': 'Best',
  'results.progressSessions': 'Scored',
  'results.progressNoScores':
    'No band scores yet — complete a session with feedback to see your trend.',
  'results.historyHeading': 'History',
  'leaderboard.title': 'Leaderboard',
  'leaderboard.subtitle': 'Average band from scored practice sessions.',
  'leaderboard.retry': 'Retry',
  'leaderboard.errorInvalid':
    'The server sent data we could not read. Check your connection or try again.',
  'leaderboard.you': 'You',
  'leaderboard.rank': 'Rank',
  'leaderboard.name': 'Name',
  'leaderboard.avg': 'Avg',
  'leaderboard.sessions': 'Sessions',
  'leaderboard.emptyTitle': 'No leaders yet',
  'leaderboard.emptyBody':
    'Be the first — complete mock exams and appear on the board.',
  'leaderboard.loadError': 'Could not load leaderboard.',
  'leaderboard.restHeading': 'Rest of the board',
  'profile.title': 'Profile',
  'profile.section.display': 'Display',
  'profile.section.account': 'Account',
  'profile.theme': 'Theme',
  'profile.theme.match': 'Match Telegram',
  'profile.theme.light': 'Light',
  'profile.theme.dark': 'Dark',
  'profile.language': 'Language',
  'profile.updatePhoto': 'Profile photo',
  'profile.updatePhotoHint':
    'Updates your name, username, and picture from Telegram (uses your current mini app session).',
  'profile.updatePhotoAction': 'Update from Telegram',
  'profile.syncing': 'Updating…',
  'profile.synced': 'Profile updated.',
  'profile.syncPhotoError':
    'Could not update. Try again or reopen the mini app from Telegram.',
  'profile.lang.en': 'English',
  'profile.lang.ru': 'Русский',
  'profile.lang.uz': 'Oʻzbekcha',
  'profile.deleteAccount': 'Delete account',
  'profile.deleteConfirmTitle': 'Delete account?',
  'profile.deleteConfirmBody':
    'Your speaking history, scores, and feedback will be removed. You can sign in again with Telegram to start fresh.',
  'profile.deleteConfirm': 'Delete',
  'profile.cancel': 'Cancel',
  'profile.deleting': 'Deleting…',
  'profile.deleteFailed': 'Could not delete account. Try again.',
}

const RU: Dict = {
  ...EN,
  'nav.home': 'Главная',
  'nav.results': 'Результаты',
  'nav.leaderboard': 'Табло',
  'nav.profile': 'Профиль',
  'app.loading': 'Вход…',
  'app.signInError': 'Не удалось войти',
  'app.tryAgain': 'Повторить',
  'common.loading': 'Загрузка…',
  'home.greet.midnight': 'Не спится?',
  'home.greet.morning': 'Доброе утро',
  'home.greet.afternoon': 'Добрый день',
  'home.greet.evening': 'Добрый вечер',
  'home.badge.available': 'Доступно',
  'home.badge.soon': 'Скоро',
  'home.badge.inDev': 'В разработке',
  'home.speaking.badge': 'Доступно',
  'home.speaking.duration': '~12 мин',
  'home.speaking.title': 'Мок‑экзамен Speaking',
  'home.speaking.blurb':
    'Три части. Экзаменатор ИИ. Подробный разбор по шкале IELTS.',
  'home.speaking.ariaHero':
    'Начать полный мок‑экзамен Speaking — три части, ИИ‑экзаменатор, разбор по шкале IELTS',
  'home.quickPractice': 'Быстрая практика',
  'home.speaking.ariaQuickToggle': 'Развернуть или свернуть быструю практику',
  'home.speaking.ariaQuickPanel': 'Варианты быстрой практики',
  'home.tile.warm.title': 'Разминка',
  'home.tile.warm.meta': '~3 мин · 2 вопроса',
  'home.tile.warm.aria': 'Разминка — 2 вопроса, около 3 минут',
  'home.tile.p1.title': 'Фокус Part 1',
  'home.tile.p1.meta': '~5 мин · знакомые темы',
  'home.tile.p1.aria': 'Part 1 — знакомые темы, около 5 минут',
  'home.tile.p2.title': 'Фокус Part 2',
  'home.tile.p2.meta': '~5 мин · карточка',
  'home.tile.p2.aria': 'Part 2 — карточка с темой, около 5 минут',
  'home.tile.p3.title': 'Фокус Part 3',
  'home.tile.p3.meta': '~5 мин · обсуждение',
  'home.tile.p3.aria': 'Part 3 — обсуждение, около 5 минут',
  'home.module.writing.title': 'Writing',
  'home.module.writing.desc': 'Task 1 + Task 2 с обратной связью ИИ.',
  'home.module.listening.title': 'Listening',
  'home.module.listening.desc': '4 секции, формат IELTS.',
  'home.module.reading.title': 'Reading',
  'home.module.reading.desc': '3 текста, 40 вопросов.',
  'home.footer': 'Оценка ИИ по официальным дескрипторам IELTS',
  'results.title': 'Результаты',
  'results.loadError': 'Не удалось загрузить список результатов.',
  'results.historyNotFound':
    'Список результатов сейчас недоступен: сервер вернул «не найдено» — часто это устаревшая версия API или неверный адрес. Проверьте интернет и попробуйте снова; если не помогает — обновите мини‑приложение.',
  'results.historyServerError':
    'Сервер временно недоступен. Попробуйте через минуту.',
  'results.historyGeneric':
    'Не удалось загрузить данные. Проверьте подключение к интернету и попробуйте снова.',
  'results.retry': 'Повторить',
  'results.emptyTitle': 'Пока нет результатов',
  'results.emptyBody':
    'Здесь будут отображаться результаты ваших мок‑экзаменов',
  'results.goHome': 'На главную',
  'results.backHome': 'На главную',
  'results.detailLoading': 'Загружаем результаты…',
  'results.detailLoadError': 'Не удалось загрузить результаты',
  'results.kind.full': 'Полный тест',
  'results.kind.warm': 'Быстрая практика',
  'results.kind.section': 'Секция',
  'results.band': 'Балл',
  'results.progressTitle': 'Динамика балла',
  'results.progressCaption':
    'Общий балл по оценённым сессиям: от старых к новым.',
  'results.progressHint':
    'Пройдите ещё одну оценённую сессию — появится линия динамики.',
  'results.progressBest': 'Лучший',
  'results.progressSessions': 'Оценено',
  'results.progressNoScores':
    'Пока нет оценённых баллов — завершите сессию с обратной связью.',
  'results.historyHeading': 'История',
  'leaderboard.title': 'Таблица лидеров',
  'leaderboard.subtitle': 'Средний балл по оценённым практическим сессиям.',
  'leaderboard.retry': 'Повторить',
  'leaderboard.errorInvalid':
    'Не удалось разобрать ответ сервера. Проверьте соединение или попробуйте позже.',
  'leaderboard.you': 'Вы',
  'leaderboard.rank': 'Место',
  'leaderboard.name': 'Имя',
  'leaderboard.avg': 'Сред.',
  'leaderboard.sessions': 'Сессий',
  'leaderboard.emptyTitle': 'Пока нет лидеров',
  'leaderboard.emptyBody': 'Станьте первым пройдя мок‑экзамены',
  'leaderboard.loadError': 'Не удалось загрузить таблицу.',
  'leaderboard.restHeading': 'Остальные участники',
  'profile.title': 'Профиль',
  'profile.section.display': 'Оформление',
  'profile.section.account': 'Аккаунт',
  'profile.theme': 'Тема',
  'profile.theme.match': 'Как в Telegram',
  'profile.theme.light': 'Светлая',
  'profile.theme.dark': 'Тёмная',
  'profile.language': 'Язык',
  'profile.updatePhoto': 'Фото профиля',
  'profile.updatePhotoHint':
    'Обновит имя, ник и фото из Telegram (текущая сессия мини‑приложения).',
  'profile.updatePhotoAction': 'Обновить из Telegram',
  'profile.syncing': 'Обновляем…',
  'profile.synced': 'Профиль обновлён.',
  'profile.syncPhotoError':
    'Не удалось обновить. Попробуйте снова или откройте мини‑приложение заново.',
  'profile.deleteAccount': 'Удалить аккаунт',
  'profile.deleteConfirmTitle': 'Удалить аккаунт?',
  'profile.deleteConfirmBody':
    'История speaking, баллы и отзывы будут удалены. Позже можно снова войти через Telegram и начать заново.',
  'profile.deleteConfirm': 'Удалить',
  'profile.cancel': 'Отмена',
  'profile.deleting': 'Удаление…',
  'profile.deleteFailed': 'Не удалось удалить аккаунт. Попробуйте снова.',
}

const UZ: Dict = {
  ...EN,
  'nav.home': 'Bosh sahifa',
  'nav.results': 'Natijalar',
  'nav.leaderboard': 'Liderlar',
  'nav.profile': 'Profil',
  'app.loading': 'Kiryapsiz…',
  'app.signInError': 'Kirish amalga oshmadi',
  'app.tryAgain': 'Qayta urinish',
  'common.loading': 'Yuklanmoqda…',
  'home.greet.midnight': 'Hali ham uyg‘oqmisiz?',
  'home.greet.morning': 'Xayrli tong',
  'home.greet.afternoon': 'Xayrli kun',
  'home.greet.evening': 'Xayrli kech',
  'home.badge.available': 'Mavjud',
  'home.badge.soon': 'Tez orada',
  'home.badge.inDev': 'Ishlab chiqilmoqda',
  'home.speaking.badge': 'Mavjud',
  'home.speaking.duration': '~12 daq',
  'home.speaking.title': 'Speaking sinov imitatsiyasi',
  'home.speaking.blurb':
    'Uch qism. AI imtihon oluvchi. IELTS shkalasi bo‘yicha batafsil tahlil.',
  'home.speaking.ariaHero':
    'Speaking sinov imitatsiyasini boshlash — uch qism, AI imtihon oluvchi, IELTS shkalasi bo‘yicha tahlil',
  'home.quickPractice': 'Tez mashq',
  'home.speaking.ariaQuickToggle': 'Tez mashq variantlarini ochish yoki yopish',
  'home.speaking.ariaQuickPanel': 'Tez mashq variantlari',
  'home.tile.warm.title': 'Isinish',
  'home.tile.warm.meta': '~3 daq · 2 savol',
  'home.tile.warm.aria': 'Isinish — 2 savol, taxminan 3 daqiqa',
  'home.tile.p1.title': '1-qism fokus',
  'home.tile.p1.meta': '~5 daq · tanish mavzular',
  'home.tile.p1.aria': '1-qism — tanish mavzular, taxminan 5 daqiqa',
  'home.tile.p2.title': '2-qism fokus',
  'home.tile.p2.meta': '~5 daq · kartochka',
  'home.tile.p2.aria': '2-qism — kartochka, taxminan 5 daqiqa',
  'home.tile.p3.title': '3-qism fokus',
  'home.tile.p3.meta': '~5 daq · muhokama',
  'home.tile.p3.aria': '3-qism — muhokama, taxminan 5 daqiqa',
  'home.module.writing.title': 'Writing',
  'home.module.writing.desc': 'Task 1 + Task 2 — AI tahlili.',
  'home.module.listening.title': 'Listening',
  'home.module.listening.desc': '4 bo‘lim, IELTS formati.',
  'home.module.reading.title': 'Reading',
  'home.module.reading.desc': '3 matn, 40 savol.',
  'home.footer': 'IELTS rasmiy deskriptorlari bo‘yicha AI baholash',
  'results.title': 'Natijalar',
  'results.loadError': 'Natijalar ro‘yxatini yuklab bo‘lmadi.',
  'results.historyNotFound':
    'Natijalar ro‘yxati hozircha mavjud emas — server yangilanmagan yoki manzil noto‘g‘ri bo‘lishi mumkin. Internetni tekshirib qayta urinib ko‘ring yoki mini‑ilovani yangilang.',
  'results.historyServerError':
    'Server vaqtincha band. Bir ozdan keyin qayta urinib ko‘ring.',
  'results.historyGeneric':
    'Yuklashda xatolik yuz berdi. Internetni tekshirib qayta urinib ko‘ring.',
  'results.retry': 'Qayta urinish',
  'results.emptyTitle': 'Hozircha natijalar yo‘q',
  'results.emptyBody': 'Mock imtihon natijalaringiz shu yerda ko‘rinadi.',
  'results.goHome': 'Bosh sahifaga',
  'results.backHome': 'Bosh sahifaga',
  'results.detailLoading': 'Natijalar yuklanmoqda…',
  'results.detailLoadError': 'Natijalarni yuklab bo‘lmadi',
  'results.kind.full': 'To‘liq test',
  'results.kind.warm': 'Tez mashq',
  'results.kind.section': 'Bo‘lim',
  'results.band': 'Ball',
  'results.progressTitle': 'Ball bo‘yicha trend',
  'results.progressCaption':
    'Baholangan seanslar bo‘yicha umumiy ball (eskidan yangisiga).',
  'results.progressHint':
    'Trend chizig‘i uchun yana bitta baholangan seansni yakunlang.',
  'results.progressBest': 'Eng yuqori',
  'results.progressSessions': 'Baholangan',
  'results.progressNoScores':
    'Hozircha ball yo‘q — tahlil bilan seansni yakunlang.',
  'results.historyHeading': 'Tarix',
  'leaderboard.title': 'Liderlar jadvali',
  'leaderboard.subtitle': 'Baholangan mashq seanslari bo‘yicha o‘rtacha ball.',
  'leaderboard.retry': 'Qayta urinish',
  'leaderboard.errorInvalid':
    'Server javobini o‘qib bo‘lmadi. Internetni tekshiring yoki keyinroq urinib ko‘ring.',
  'leaderboard.you': 'Siz',
  'leaderboard.rank': 'O‘rin',
  'leaderboard.name': 'Ism',
  'leaderboard.avg': "O'rt.",
  'leaderboard.sessions': 'Seanslar',
  'leaderboard.emptyTitle': 'Hozircha liderlar yo‘q',
  'leaderboard.emptyBody':
    'Birinchi bo‘ling — mock imtihonlarni topshirib jadvalda paydo bo‘ling.',
  'leaderboard.loadError': 'Jadvalni yuklab bo‘lmadi.',
  'leaderboard.restHeading': 'Boshqa ishtirokchilar',
  'profile.title': 'Profil',
  'profile.section.display': 'Ko‘rinish',
  'profile.section.account': 'Hisob',
  'profile.theme': 'Mavzu',
  'profile.theme.match': 'Telegram kabi',
  'profile.theme.light': 'Yorug‘',
  'profile.theme.dark': 'Qorong‘u',
  'profile.language': 'Til',
  'profile.updatePhoto': 'Profil fotosi',
  'profile.updatePhotoHint':
    'Telegramdan ism, username va rasmni yangilaydi (joriy mini‑ilova sessiyasi).',
  'profile.updatePhotoAction': 'Telegramdan yangilash',
  'profile.syncing': 'Yangilanmoqda…',
  'profile.synced': 'Profil yangilandi.',
  'profile.syncPhotoError':
    'Yangilab bo‘lmadi. Qayta urining yoki mini‑ilovani Telegramdan qayta oching.',
  'profile.deleteAccount': 'Hisobni o‘chirish',
  'profile.deleteConfirmTitle': 'Hisob o‘chirilsinmi?',
  'profile.deleteConfirmBody':
    'Speaking tarixi, ballar va fikrlar o‘chiriladi. Keyinroq Telegram orqali qayta kirishingiz mumkin.',
  'profile.deleteConfirm': 'O‘chirish',
  'profile.cancel': 'Bekor qilish',
  'profile.deleting': 'O‘chirilmoqda…',
  'profile.deleteFailed': 'Hisobni o‘chirib bo‘lmadi. Qayta urinib ko‘ring.',
}

const MAP: Record<AppLocale, Dict> = { en: EN, ru: RU, uz: UZ }

export function t(locale: AppLocale, key: string): string {
  return MAP[locale][key] ?? EN[key] ?? key
}
